import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Building2,
  ChevronDown,
  ChevronRight,
  Filter,
  RefreshCw,
  Search,
  Tag,
  X,
} from 'lucide-react';
import { MinimalCard, CleanButton } from 'components/styled';
import { SearchInput } from 'components/SearchInput';
import useUrl from 'hooks/useUrl';
import { csmList } from 'CsmModule/constants';
import { TenantTypes, tenantTypeConfigs } from 'TenantModule/constants';
import { FetchTenantPayload } from 'TenantModule/types';

const ALL_TENANT_TYPES = '';

export type TenantsSearchSubmitPayload = {
  /**
   * Applied client-side against tenant names (case-insensitive) — the
   * backend's tenantName LIKE filter is case-sensitive, and the full tenant
   * list is already loaded
   */
  searchText: string;
  /**
   * Structured filters for POST /tenant/getAll. undefined = keep the
   * previously submitted filters (text-only submits); null = cleared
   */
  filterParams?: FetchTenantPayload | null;
};

type TenantsSearchFiltersProps = {
  onSubmit: (payload: TenantsSearchSubmitPayload) => void;
  isLoading?: boolean;
};

/**
 * Search bar + expandable advanced filters for the Tenants page, following
 * the TeamsV2Page/MembersPage pattern: draft filter state applies only on
 * explicit submit (Enter or Search button).
 */
const TenantsSearchFilters = ({ onSubmit, isLoading }: TenantsSearchFiltersProps) => {
  const theme = useTheme();
  const { queryParams, pushQueryParams } = useUrl();

  const [searchInput, setSearchInput] = useState<string>(queryParams.get('q') || '');
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [tenantType, setTenantType] = useState<TenantTypes | typeof ALL_TENANT_TYPES>(
    ALL_TENANT_TYPES,
  );
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [csm, setCsm] = useState<string>('');

  // Keep the applied search in sync with the URL `q` param (initial load,
  // back/forward navigation, in-app links). Guarded by a ref because
  // queryParams is rebuilt on every render.
  const prevUrlSearchRef = useRef<string | null>(null);
  useEffect(() => {
    const urlSearchText = queryParams.get('q') || '';
    if (prevUrlSearchRef.current === urlSearchText) return;
    const isFirstRun = prevUrlSearchRef.current === null;
    prevUrlSearchRef.current = urlSearchText;
    if (isFirstRun && !urlSearchText) return;
    setSearchInput(urlSearchText);
    onSubmit({ searchText: urlSearchText });
  }, [queryParams, onSubmit]);

  const buildFilterParams = (): FetchTenantPayload | null => {
    const params: FetchTenantPayload = {
      ...(csm ? { csm } : {}),
      ...(tenantType ? { tenantType } : {}),
      ...(tags.length ? { tags } : {}),
    };
    return Object.keys(params).length ? params : null;
  };

  const onSearch = () => {
    if (isLoading) return;
    const searchText = searchInput.trim();
    setSearchInput(searchText);
    prevUrlSearchRef.current = searchText;
    const mergedParams = Object.fromEntries(queryParams.entries());
    if (searchText) mergedParams.q = searchText;
    else delete mergedParams.q;
    pushQueryParams(mergedParams);
    onSubmit({ searchText, filterParams: buildFilterParams() });
  };

  const onReset = () => {
    // Same in-flight guard as onSearch — overlapping /tenant/getAll responses
    // could otherwise land out of order and show stale results
    if (isLoading) return;
    setTenantType(ALL_TENANT_TYPES);
    setTags([]);
    setTagInput('');
    setCsm('');
    setSearchInput('');
    prevUrlSearchRef.current = '';
    const mergedParams = Object.fromEntries(queryParams.entries());
    delete mergedParams.q;
    pushQueryParams(mergedParams);
    onSubmit({ searchText: '', filterParams: null });
  };

  const handleAddTag = () => {
    // The backend interpolates tags into a SQL array literal, so quotes
    // would break the query
    const value = tagInput.trim().replace(/'/g, '');
    if (!value) return;
    setTags((prev) => (prev.includes(value) ? prev : [...prev, value]));
    setTagInput('');
  };

  const removeTag = (value: string) => setTags((prev) => prev.filter((tag) => tag !== value));

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (csm) count++;
    if (tenantType) count++;
    if (tags.length) count++;
    return count;
  }, [csm, tenantType, tags]);

  return (
    <>
      {/* Search and Quick Actions */}
      <MinimalCard $hoverEffect={false} mb={3}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <SearchInput
            fullWidth
            placeholder="Search by tenant name…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSearch();
            }}
            InputProps={{
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => setSearchInput('')}
                  >
                    <X size={16} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 300 }}
          />

          <CleanButton
            variant="contained"
            startIcon={<Search size={18} />}
            onClick={onSearch}
            disabled={isLoading}
          >
            Search
          </CleanButton>

          <CleanButton
            variant="outlined"
            startIcon={filtersExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            endIcon={
              activeFilterCount > 0 ? (
                <Box
                  sx={{
                    background: theme.palette.primary.main,
                    color: 'white',
                    borderRadius: '50%',
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                  }}
                >
                  {activeFilterCount}
                </Box>
              ) : undefined
            }
            onClick={() => setFiltersExpanded((prev) => !prev)}
          >
            Filters
          </CleanButton>

          <CleanButton variant="outlined" startIcon={<RefreshCw size={18} />} onClick={onReset}>
            Reset
          </CleanButton>
        </Box>
      </MinimalCard>

      {/* Expandable Filters */}
      <Collapse in={filtersExpanded}>
        <MinimalCard $hoverEffect={false} mb={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box display="flex" alignItems="center" gap={1}>
              <Filter size={20} color={theme.palette.primary.main} />
              <Typography variant="h6" fontWeight={600}>
                Advanced Filters
              </Typography>
            </Box>
            {activeFilterCount > 0 && (
              <CleanButton
                size="small"
                variant="text"
                startIcon={<X size={16} />}
                onClick={onReset}
              >
                Clear All
              </CleanButton>
            )}
          </Box>

          {/* Organization */}
          <FilterSection mb={3}>
            <FilterSectionTitle icon={<Building2 size={16} />} title="Organization" />
            <Box
              display="grid"
              gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
              gap={2}
              alignItems="center"
            >
              <FormControl fullWidth variant="outlined">
                <InputLabel id="csm-select-label">CSM</InputLabel>
                <Select
                  labelId="csm-select-label"
                  label="CSM"
                  value={csm}
                  onChange={(e) => setCsm(String(e.target.value || ''))}
                >
                  <MenuItem value="">All</MenuItem>
                  {csmList.map((csmOption) => (
                    <MenuItem key={csmOption.accountId} value={csmOption.name}>
                      {csmOption.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                select
                fullWidth
                label="Tenant Type"
                value={tenantType}
                onChange={(e) =>
                  setTenantType(e.target.value as TenantTypes | typeof ALL_TENANT_TYPES)
                }
              >
                <MenuItem value={ALL_TENANT_TYPES}>All</MenuItem>
                {Object.values(tenantTypeConfigs).map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </FilterSection>

          {/* Tags */}
          <FilterSection>
            <FilterSectionTitle icon={<Tag size={16} />} title="Tags" />
            <TextField
              fullWidth
              size="small"
              label="Tag"
              placeholder="Enter tag and press Enter"
              helperText="Matches tenants containing all of the tags"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddTag();
              }}
            />
            {tags.length > 0 && (
              <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                {tags.map((tag) => (
                  <Chip key={tag} color="primary" label={tag} onDelete={() => removeTag(tag)} />
                ))}
              </Box>
            )}
          </FilterSection>
        </MinimalCard>
      </Collapse>
    </>
  );
};

const FilterSection = ({ children, mb }: { children: React.ReactNode; mb?: number }) => (
  <Box
    sx={{
      padding: 3,
      borderRadius: 2,
      // Matches TeamsV2Page's FilterSection border colors
      border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? '#1f1f1f' : '#e5e5e5'}`,
      mb,
    }}
  >
    {children}
  </Box>
);

const FilterSectionTitle = ({ icon, title }: { icon: React.ReactNode; title: string }) => (
  <Typography
    variant="subtitle2"
    sx={{
      mb: 2,
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      gap: 1,
    }}
  >
    {icon} {title}
  </Typography>
);

export default TenantsSearchFilters;
